import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "input-container" }

import { ref, inject, watch, computed, PropType } from "vue";
import FindPostcodeModal from "./find-postcode-modal.vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { AddressLookupResult } from "@/types/address-lookup/AddressLookupResult";
import { AddressLookupQuery } from "@/types/address-lookup/AddressLookupQuery";
import { getErrorMessage } from "bright-logic-core/src/errors";
import displayErrorMessage from "@/utils/ErrorHandling";
import { DxButton } from "devextreme-vue/text-box";
import { CountryCode } from "bright-logic-core/src/CountryCode";
import { FieldValidation } from "acquaint-api/validation/FieldValidation";
import AcquaintTextbox from "../../inputs/acquaint-textbox.vue";
import { FieldSize } from "@/enums/system/FieldSize";
import { DxValidator } from "devextreme-vue";
import { DxCustomRule, DxRequiredRule } from "devextreme-vue/validator";
import { ValidatedEvent } from "devextreme/ui/validator";
import { validationCallback } from "@/utils/validation/FieldValidator";
import { validatePostcode } from "@/utils/validation/FieldValidator";


export default /*#__PURE__*/_defineComponent({
  __name: 'postcode-lookup',
  props: {
  /**
   * The countryCode allowed value for the input.
   * @type {number}
   * @required
   */
  countryCode: {
    type: Number,
    required: true,
  },
  /**
   * The string value used with `v-model` to control the component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: String,
    required: true,
  },
  /**
   * The validation rules and constraints applied to the field. Defines the validation logic to ensure correct user input.
   * @type {FieldValidation}
   * @required
   */
  fieldValidation: {
    type: Object as PropType<FieldValidation>,
    required: true,
  },
  /**
   * Determines whether the specific control is read only.
   * @type {boolean}
   * @default false
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /**
   * The width of the field, defined by the `FieldSize` enum.
   * @type {FieldSize}
   * @default FieldSize.Full
   */
  width: {
    type: String as PropType<FieldSize>,
    default: FieldSize.Full,
  },
  /**
   * The validation group name used for form validation.
   * @type {string}
   * @default ''
   */
  validationGroup: {
    type: String,
    default: "",
  },
  /**
   * The label displayed for the postcode control.
   * @type {String}
   * @default ''
   */
  label: {
    type: String,
    default: "",
  },
  /**
   * The boolean value indicates whether allow to automatically select address.
   * @type {boolean}
   * @default true
   */
  autoSelectAddress: {
    type: Boolean,
    default: true,
  },
},
  emits: ["update:modelValue", "selectAddress"],
  setup(__props, { emit }) {

const props = __props;

_useCssVars(_ctx => ({
  "ffc5901e": (props.width)
}))

const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const showModal = ref(false);
const addresses = ref<AddressLookupResult[]>();
const searching = ref(false);



const postcode = computed({
  get: () => props.modelValue,
  set: (e: string) => emit("update:modelValue", e),
});



/** Handles the address selected event of the find postcode modal */
const onPostcodeSelected = (e: AddressLookupResult) => {
  emit("selectAddress", e);
};

const onSearch = async () => {
  // Avoid unnecessary api calls when no postcode entered
  if (!postcode.value) {
    return;
  }

  searching.value = true;

  // For backwards compatibility with Acquaint App, ensure modal is shown when searching for an address without auto selecting
  if (!props.autoSelectAddress) {
    showModal.value = true;
  }

  const query: AddressLookupQuery = {
    postcode: postcode.value,
    countryCode: props.countryCode,
  };

  // Get results back from api
  try {
    addresses.value = await apiClient.httpPost<AddressLookupResult[]>(
      "App/AddressLookup",
      query,
    );
  } catch (error) {
    displayErrorMessage(getErrorMessage(error));
  }

  // To improve UX, auto select first result, if option has been set
  if (props.autoSelectAddress && addresses.value && singleLookupResult.value) {
    emit("selectAddress", addresses.value[0]);
    return;
  }

  showModal.value = true;
  searching.value = false;
};

/* Determines whether the api returned a single lookup result
 * Need to check on display address as sometimes the api returns an array with 1 result but no data */
const singleLookupResult = computed(
  () =>
    addresses.value &&
    addresses.value.length === 1 &&
    addresses.value[0].displayAddress,
);

const rowClick = (e: AddressLookupResult) => {
  emit("selectAddress", e);
  addresses.value = [];
  showModal.value = false;
};

watch(
  () => showModal.value,
  () => {
    // When closing reset previous address results to avoid confusion when next lookup is perfromed
    if (!showModal.value) {
      addresses.value = [];
    }
  },
);

const computedLabel = computed(() =>
  props.countryCode === CountryCode.Ireland ? "Eircode" : "Postcode",
);

const buttonOptions = computed(() => {
  return {
    icon: "search",
    stylingMode: "text",
    type: "dx-dropdowneditor-button",
    disabled: props.readOnly,
    onClick: async () => await onSearch(),
  };
});

/** Callback function for postcode validation. This can't be handled by normal validation as it changes depending on country code */
const postcodeValidationCallback = () =>
  validatePostcode(postcode.value, props.countryCode);
const requiredMessage = computed(() => `Postcode is required`);

/** Custom validation configuration to override DevExpress default validation method */
const adapterConfig = {
  getValue: () => {
    return postcode.value;
  },
  applyValidationResults: (e: ValidatedEvent) => {
    isValid.value = !!e.isValid;

    // Construct list of validation errors to be displayed to the user when they hover over the field
    validationErrors.value =
      !e.isValid && e.brokenRule?.message ? e.brokenRule.message : "";
  },
  validationRequestsCallbacks: [],
};

const validationErrors = ref("");
const isValid = ref(true);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", null, _toDisplayString(props.label ? props.label : computedLabel.value), 1),
      _createVNode(AcquaintTextbox, {
        modelValue: postcode.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((postcode).value = $event)),
        "field-validation": __props.fieldValidation,
        "custom-validation": postcodeValidationCallback,
        readOnly: __props.readOnly,
        width: __props.width
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(DxButton), {
            name: "addressLookup",
            position: "after",
            options: buttonOptions.value
          }, null, 8, ["options"]),
          _createVNode(_unref(DxValidator), {
            adapter: adapterConfig,
            "validation-group": props.validationGroup
          }, {
            default: _withCtx(() => [
              (__props.fieldValidation.isRequired)
                ? (_openBlock(), _createBlock(_unref(DxRequiredRule), {
                    key: 0,
                    message: requiredMessage.value
                  }, null, 8, ["message"]))
                : _createCommentVNode("", true),
              _createVNode(_unref(DxCustomRule), {
                fieldValidation: __props.fieldValidation,
                "validation-callback": _unref(validationCallback),
                reevaluate: true
              }, null, 8, ["fieldValidation", "validation-callback"])
            ]),
            _: 1
          }, 8, ["validation-group"])
        ]),
        _: 1
      }, 8, ["modelValue", "field-validation", "readOnly", "width"])
    ]),
    _createVNode(FindPostcodeModal, {
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
      onPostcodeSelected: onPostcodeSelected,
      data: addresses.value,
      onRowClick: rowClick,
      searching: searching.value
    }, null, 8, ["modelValue", "data", "searching"])
  ], 64))
}
}

})